import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useUserId } from '~/components/user/SessionProvider'
import {
  getContactsForAssetEvents,
  getEventById,
  getEventsByAssetId,
  getEventsByContactId,
} from './readEvents'
import {
  addContactToEvent,
  createEvent,
  deleteEvent,
  removeContactFromEvent,
  updateEvent,
  type CreateEventPayload,
  type UpdateEventPayload,
} from './writeEvents'

export const EVENTS_QUERY_KEY = (userId: string) => ({
  all: [userId, 'events'] as const,
  forAsset: (assetId: string) => [userId, 'events', 'asset', assetId] as const,
  forContact: (contactId: string) => [userId, 'events', 'contact', contactId] as const,
  detail: (eventId: string) => [userId, 'events', eventId] as const,
  assetEventContacts: (assetId: string) =>
    [userId, 'events', 'asset', assetId, 'contacts'] as const,
})

export function useAssetEvents(assetId: string) {
  const userId = useUserId()

  return useQuery({
    queryKey: EVENTS_QUERY_KEY(userId).forAsset(assetId),
    queryFn: () => getEventsByAssetId(assetId),
    enabled: !!assetId && !!userId,
  })
}

export function useContactEvents(contactId: string) {
  const userId = useUserId()

  return useQuery({
    queryKey: EVENTS_QUERY_KEY(userId).forContact(contactId),
    queryFn: () => getEventsByContactId(contactId),
    enabled: !!contactId && !!userId,
  })
}

export function useEvent(eventId: string) {
  const userId = useUserId()

  return useQuery({
    queryKey: EVENTS_QUERY_KEY(userId).detail(eventId),
    queryFn: () => getEventById(eventId),
    enabled: !!eventId && !!userId,
  })
}

export function useAssetEventContacts(assetId: string) {
  const userId = useUserId()

  return useQuery({
    queryKey: EVENTS_QUERY_KEY(userId).assetEventContacts(assetId),
    queryFn: () => getContactsForAssetEvents(assetId),
    enabled: !!assetId && !!userId,
  })
}

export function useCreateEvent() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation({
    mutationFn: (payload: CreateEventPayload) => createEvent(payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: EVENTS_QUERY_KEY(userId).forAsset(variables.assetId),
      })
    },
  })
}

export function useUpdateEvent() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation({
    mutationFn: (payload: UpdateEventPayload) => updateEvent(payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: EVENTS_QUERY_KEY(userId).detail(variables.eventId),
      })
      void queryClient.invalidateQueries({
        queryKey: EVENTS_QUERY_KEY(userId).all,
      })
    },
  })
}

export function useDeleteEvent() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation({
    mutationFn: (eventId: string) => deleteEvent(eventId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: EVENTS_QUERY_KEY(userId).all,
      })
    },
  })
}

export function useAddContactToEvent() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation({
    mutationFn: ({ eventId, contactId }: { eventId: string; contactId: string }) =>
      addContactToEvent(eventId, contactId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: EVENTS_QUERY_KEY(userId).detail(variables.eventId),
      })
      void queryClient.invalidateQueries({
        queryKey: EVENTS_QUERY_KEY(userId).all,
      })
    },
  })
}

export function useRemoveContactFromEvent() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation({
    mutationFn: ({ eventId, contactId }: { eventId: string; contactId: string }) =>
      removeContactFromEvent(eventId, contactId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: EVENTS_QUERY_KEY(userId).detail(variables.eventId),
      })
      void queryClient.invalidateQueries({
        queryKey: EVENTS_QUERY_KEY(userId).all,
      })
    },
  })
}
